import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock } from "lucide-react";
import SectionReveal from "@/components/effects/SectionReveal";
import { BLOG_CATEGORIES, BLOG_POSTS } from "@/data/content";
import { TID } from "@/constants/testIds";

const Blog = () => {
    const [cat, setCat] = useState("All");
    const filtered = useMemo(
        () => (cat === "All" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === cat)),
        [cat]
    );

    return (
        <section id="insights" className="relative py-24 md:py-40">
            <div className="container-x">
                <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <SectionReveal>
                        <p className="overline">07 — Insights</p>
                        <h2
                            className="mt-4 text-3xl font-medium tracking-tight text-white sm:text-4xl md:text-5xl"
                            style={{ fontFamily: "'General Sans', sans-serif" }}
                        >
                            Field notes from the build.
                        </h2>
                    </SectionReveal>
                    <SectionReveal delay={0.15}>
                        <p className="max-w-md text-sm text-brand-muted md:text-base">
                            A journal of what actually works in web, growth, and business — coming soon. Structured for
                            SEO, written for practitioners.
                        </p>
                    </SectionReveal>
                </div>

                <SectionReveal delay={0.2}>
                    <div className="mt-10 flex flex-wrap gap-2">
                        {BLOG_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                data-testid={TID.blogCategoryChip(c)}
                                onClick={() => setCat(c)}
                                className={`inline-flex h-9 items-center rounded-full border px-3.5 text-xs font-medium tracking-tight transition-colors ${
                                    cat === c
                                        ? "border-white/30 bg-white/[.08] text-white"
                                        : "border-white/[.08] bg-white/[.02] text-brand-muted hover:border-white/20 hover:text-white"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </SectionReveal>

                <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <AnimatePresence mode="popLayout">
                        {filtered.map((post, i) => (
                            <motion.article
                                key={post.title}
                                data-testid={TID.blogPost(i)}
                                layout
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                                className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/[.08] bg-[#0a0a0a] p-7 card-lift"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="chip">{post.category}</span>
                                    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-brand-muted">
                                        <Clock className="h-3 w-3" /> Coming soon
                                    </span>
                                </div>
                                <h3
                                    className="mt-8 text-xl font-medium tracking-tight text-white md:text-2xl"
                                    style={{ fontFamily: "'General Sans', sans-serif" }}
                                >
                                    {post.title}
                                </h3>
                                <p className="mt-3 text-sm text-brand-muted">
                                    A deep, opinionated write-up is being drafted. Structured for SEO and written for
                                    people who ship.
                                </p>
                                <div className="mt-8 flex items-center gap-3 border-t border-white/[.06] pt-6 text-xs text-brand-muted">
                                    <span
                                        className="relative flex h-2 w-2"
                                        aria-label="In progress"
                                    >
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-primary opacity-50" />
                                        <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-primary" />
                                    </span>
                                    In the pipeline
                                </div>
                            </motion.article>
                        ))}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
};

export default Blog;
